import { useEffect, useRef, useState } from "react";
import { useLocalStorageState } from "../../../../../hooks/useLocalStorageState";
import {
  INTEGRATION_SYNC_ALL_KEY,
  INTEGRATION_SYNC_NEW_KEY,
  REFETCH_INTERVAL_MS,
  UNKNOWN_AUTHORIZATION_DATE,
  MESSAGE_TYPE,
} from "../config";
import { useConstantContactAuthDataQuery } from "../../../../../api/constantContact/useConstantContactAuthDataQuery";
import { uiNotification } from "../../../../../services/UINotificationService";
import { useSaveConstantContactAuthDataMutation } from "../../../../../api/constantContact/useSaveConstantContactAuthDataMutation";

const parseBoolean = (value) => {
  return value === "true";
};

const isReturnUrlMessage = (message) => {
  return (
    message &&
    message.type === MESSAGE_TYPE &&
    message.childWindow === "response" &&
    typeof message.query === "object" &&
    message.query !== null
  );
};

export function useIntegration() {
  const [isEnabled, setIsEnabled] = useState(false);
  const [url, setUrl] = useState("");
  const [date, setDate] = useState(null);
  const [enableRefetchInterval, setEnableRefetchInterval] = useState(false);
  const [windowRef, setWindowRef] = useState(null);
  const isSavingRef = useRef(false);

  const { mutateAsync: saveIntegrationData } =
    useSaveConstantContactAuthDataMutation({
      onError: (error) => {
        const message =
          error?.response?.data?.message ||
          "Unable to save integration data. Try again later.";
        uiNotification.error(message);
      },
    });

  const [syncAll, setSyncAll] = useLocalStorageState(
    INTEGRATION_SYNC_ALL_KEY,
    false,
    {
      transform: parseBoolean,
      enableInit: true,
    },
  );

  const [syncNew, setSyncNew] = useLocalStorageState(
    INTEGRATION_SYNC_NEW_KEY,
    false,
    {
      transform: parseBoolean,
      enableInit: true,
    },
  );

  const { isLoading } = useConstantContactAuthDataQuery({
    refetchInterval: enableRefetchInterval ? REFETCH_INTERVAL_MS : false,
    refetchIntervalInBackground: enableRefetchInterval,
    onError: () => {
      uiNotification.error(
        "Unable to fetch integration data. Try again later.",
      );
    },
    onSuccess: (data) => {
      const enabled = data.isAuth;
      if (!enabled && !enableRefetchInterval) {
        setIsEnabled(false);
      }
      if (enabled) {
        setIsEnabled(true);
        setEnableRefetchInterval(false);
        if (windowRef && !windowRef.closed) {
          windowRef.close();
        }
        setWindowRef(null);
      }
      setUrl(data.url);
      setDate(enabled ? data.date || UNKNOWN_AUTHORIZATION_DATE : null);
      setSyncAll(data.syncAll);
      setSyncNew(data.syncNew);
    },
  });

  const onAuthorize = () => {
    isSavingRef.current = false;
    const newWindow = window.open(
      url,
      "Ratting",
      "width=550,height=550,left=150,top=200,toolbar=0,status=0,scrollbars=1",
    );
    if (newWindow) {
      setWindowRef(newWindow);
      setEnableRefetchInterval(true);
    }
  };

  useEffect(() => {
    if (!windowRef) {
      return;
    }

    const handleMessage = (event) => {
      if (event.source !== windowRef) {
        return;
      }

      const message = event.data;
      if (!isReturnUrlMessage(message)) {
        return;
      }

      const { code, state } = message.query;
      if (!code || isSavingRef.current) {
        return;
      }

      isSavingRef.current = true;

      saveIntegrationData({
        code,
        state,
        isEnabled: true,
        syncAll,
        syncNew,
      })
        .then(() => {
          if (windowRef && !windowRef.closed) {
            windowRef.close();
          }
        })
        .catch(() => undefined)
        .finally(() => {
          isSavingRef.current = false;
        });
    };

    window.addEventListener("message", handleMessage);

    const interval = setInterval(() => {
      if (windowRef.closed) {
        clearInterval(interval);
        window.removeEventListener("message", handleMessage);
        setTimeout(() => {
          setEnableRefetchInterval(false);
          setWindowRef(null);
        }, REFETCH_INTERVAL_MS);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      window.removeEventListener("message", handleMessage);
    };
  }, [windowRef, syncAll, syncNew]);

  const handleIsEnabled = (next) => {
    const next_ = typeof next === "function" ? next(isEnabled) : next;
    if (next_) {
      setIsEnabled(true);
    } else {
      setIsEnabled(false);
      setEnableRefetchInterval(false);
      saveIntegrationData({
        code: null,
        state: null,
        isEnabled: false,
        syncAll: false,
        syncNew: false,
      }).catch(() => {
        setIsEnabled(true);
      });
    }
  };

  useEffect(() => {
    if (isEnabled && !isLoading && !enableRefetchInterval) {
      saveIntegrationData({
        code: null,
        state: null,
        isEnabled: true,
        syncAll,
        syncNew,
      });
    }
  }, [syncAll, syncNew]);

  useEffect(() => {
    if (!isEnabled) {
      setEnableRefetchInterval(false);
    }
  }, [isEnabled]);

  return {
    isEnabled,
    setIsEnabled: handleIsEnabled,
    syncAll,
    setSyncAll,
    syncNew,
    setSyncNew,
    isLoading,
    date,
    onAuthorize,
  };
}
